import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { CurrentUsage } from 'src/api-sdk';
import { useAPI } from 'src/util/use-api';
import { useAuth } from 'src/util/use-auth';
import { QUERIES } from 'src/util/globals';

/**
 * Client-side gating derived from GET /billing/current_usage.
 *
 * The backend hard-caps credit-consuming actions and returns HTTP 403
 * (`credits_exhausted`) once a budget is hit or the billing period has expired
 * without automatic renewal. These helpers pre-compute the same state so the UI
 * can disable actions before the user hits that 403.
 */

export const SUPPORT_EMAIL = 'support@businessradar.com';
/** Where "Raise a support request" CTAs point. */
export const SUPPORT_REQUEST_PATH = '/feedback/support_requests/new';

export const CREDIT_KINDS = [
  'company',
  'ubo_call',
  'compliance_check',
] as const;
export type CreditKind = (typeof CREDIT_KINDS)[number];

export type BlockReason = 'hard_cap' | 'expired_no_renewal';

const usageOf = (u: CurrentUsage, kind: CreditKind): number =>
  u[`${kind}_usage`];
const budgetOf = (u: CurrentUsage, kind: CreditKind): number | null =>
  u[`${kind}_usage_budget`];

export function isExpiredNoRenewal(u: CurrentUsage): boolean {
  return new Date(u.end_date).getTime() < Date.now() && !u.automatical_renewal;
}

/** Remaining credits for a kind. Unlimited budget (null) => Infinity. */
export function remaining(u: CurrentUsage, kind: CreditKind): number {
  const budget = budgetOf(u, kind);
  return budget == null ? Infinity : Math.max(budget - usageOf(u, kind), 0);
}

/**
 * True when usage is at/over `ratio` of the budget. This is the amber "warning"
 * state and applies to soft caps too — only the red "blocked" state (isAtCap)
 * depends on hard-cap enforcement.
 */
export function approachingCap(
  u: CurrentUsage,
  kind: CreditKind,
  ratio = 0.85
): boolean {
  const budget = budgetOf(u, kind);
  return budget != null && budget > 0 && usageOf(u, kind) / budget >= ratio;
}

/** Usage as a whole-number percentage of budget (0-100), or null when there's no limit. */
export function usagePercent(u: CurrentUsage, kind: CreditKind): number | null {
  const budget = budgetOf(u, kind);
  if (budget == null || budget <= 0) return null;
  return Math.min(100, Math.round((usageOf(u, kind) / budget) * 100));
}

/** True when the cap is enforced and usage has reached the budget (red state). */
export function isAtCap(u: CurrentUsage, kind: CreditKind): boolean {
  const budget = budgetOf(u, kind);
  return (
    budget != null &&
    u.is_overusage_hard_restricted &&
    usageOf(u, kind) >= budget
  );
}

/** Kinds that the backend will reject right now. */
export function blockedKinds(u: CurrentUsage): CreditKind[] {
  if (isExpiredNoRenewal(u)) return [...CREDIT_KINDS];
  if (!u.is_overusage_hard_restricted) return [];
  return CREDIT_KINDS.filter((k) => isAtCap(u, k));
}

export function isKindBlocked(u: CurrentUsage, kind: CreditKind): boolean {
  if (isExpiredNoRenewal(u)) return true;
  return u.is_overusage_hard_restricted && isAtCap(u, kind);
}

export interface Gate {
  blocked: boolean;
  reason?: BlockReason;
  /** Tooltip copy explaining why the action is disabled. */
  tooltip?: string;
}

const OPEN_GATE: Gate = { blocked: false };

/**
 * Credit kinds the active profile can actually use. A kind that's disabled for
 * the profile (UBO / compliance) has no billing surface — its usage widget,
 * gating and banner entries should all be suppressed. `company` is always on.
 */
export function useEnabledCreditKinds(): CreditKind[] {
  const { user } = useAuth();
  // Billing/enablement is per active profile, not per user account.
  const profile = user?.active_profile;
  return CREDIT_KINDS.filter((kind) => {
    if (kind === 'ubo_call') return !!profile?.is_ubo_enabled;
    if (kind === 'compliance_check') return !!profile?.is_compliance_enabled;
    return true;
  });
}

/**
 * Fetch the current billing-period usage. Shares the react-query cache key with
 * the Usage tab so both stay in sync (and a 403 backstop invalidation refreshes
 * every consumer at once).
 */
export function useCurrentUsage() {
  const { API, authHeaderSet } = useAPI();
  return useQuery({
    queryKey: [QUERIES.BILLING_STATISTICS],
    queryFn: async () =>
      (await API?.billingAPI.billingCurrentUsageRetrieve())?.data,
    enabled: authHeaderSet,
  });
}

/**
 * Pre-gating hook for credit-consuming actions. `gateFor(kind)` returns whether
 * the action should be disabled plus the tooltip explaining why.
 */
export function useBillingGate() {
  const { t } = useTranslation('billing');
  const { data: usage } = useCurrentUsage();
  const enabledKinds = useEnabledCreditKinds();

  const kindLabel = (kind: CreditKind): string => {
    switch (kind) {
      case 'company':
        return t('company');
      case 'ubo_call':
        return t('UBO');
      case 'compliance_check':
        return t('compliance check');
    }
  };

  const gateFor = (kind: CreditKind): Gate => {
    if (!usage || !isKindBlocked(usage, kind)) return OPEN_GATE;
    if (isExpiredNoRenewal(usage)) {
      return {
        blocked: true,
        reason: 'expired_no_renewal',
        tooltip: t('Your plan has ended.'),
      };
    }
    return {
      blocked: true,
      reason: 'hard_cap',
      tooltip: t('You have used all your {{kind}} credits.', {
        kind: kindLabel(kind),
      }),
    };
  };

  return {
    usage,
    kindLabel,
    enabledKinds,
    // Only surface caps for kinds the profile can actually use.
    blockedKinds: usage
      ? blockedKinds(usage).filter((k) => enabledKinds.includes(k))
      : [],
    // Enabled kinds approaching their cap but not yet blocked — the amber
    // "warning" rung of the severity ladder.
    warnKinds:
      usage && !isExpiredNoRenewal(usage)
        ? enabledKinds.filter(
            (k) => approachingCap(usage, k) && !isKindBlocked(usage, k)
          )
        : [],
    expiredNoRenewal: usage ? isExpiredNoRenewal(usage) : false,
    gateFor,
  };
}
